/**
 * 3-D models on the map — a three.js scene drawn inside MapLibre's render pass (custom layer).
 * First residents: players, as Kenney's CC0 "Blocky Characters" (public/models/characters), one
 * character per player chosen by a hash of their id so it is stable and random-looking.
 *
 * Placement is MapLibre's own: `transform.getMatrixForModel(lngLat, altitude)` gives a model→world
 * matrix that already includes the globe's orientation and the metre scale, and
 * `defaultProjectionData.mainMatrix` is world→clip for this frame.
 *
 * Precision: MapLibre's world is 0..1 units wide (the whole earth), so a 34 m site is ~1e-6 units at a
 * position of ~0.5. Multiplying that in float32 on the GPU rounds to metres, and the rounding changes
 * with every camera matrix, so the models shimmered while rotating (Cam, 2026-09-03: "they flicker
 * when you rotate"; measured as ~3x the changed pixels of a rotation with models off). Fix: instance
 * matrices are stored relative to an origin near the view centre (small numbers survive float32), and
 * the origin is folded into the projection matrix in float64 on the CPU each frame. One InstancedMesh per character
 * model, so 18 draw calls cover every player. Instance matrices are rebuilt only when the camera
 * moves or players move; the render itself is a plain scene draw.
 *
 * LOD: models from zoom MODEL_MIN_ZOOM up; below that the existing sprites carry on and this layer
 * draws nothing. Size is screen-constant (a character is always ~CHAR_PX tall) so it reads at any
 * zoom without becoming a giant at street level — one rule for every model, stepped by half a zoom level.
 */

import * as THREE from "three";
import { FLIGHT_KINDS, buildRocket, buildSite, type FlightKind } from "./site-geometry";
export { buildSite } from "./site-geometry";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import maplibregl, { type Map as MLMap, type CustomLayerInterface } from "maplibre-gl";
import { world } from "./store.svelte";
import { CHARACTERS, characterFor } from "./characters";
import { separated } from "./separate";
import { distanceKm, missileFlight, move, type MapFilters, type MapSettings, type Pos } from "./map";
import type { ArcsLayer } from "./arcs-gl";

export const MODEL_MIN_ZOOM = 10;
const MAX_PER_MODEL = 512;
// ONE scale rule for every static model (Cam, 2026-09-03: "a single item, not resizing independently of each other").
// Sites and characters are a FIXED world size: just under the 100 m build separation, and they never scale with zoom
// (Cam, 2026-09-05: "I don't like the model resizing"). Rockets keep their own screen-sized rule below.
const UNIT_M = 80, CHAR_UNITS = 1.0;
// rockets are screen-sized like the sprites they replaced, with no world floor: the 34 m site floor made a rocket a fixed
// 100 m object at street zoom, hundreds of pixels long (Cam, 2026-09-03: "max size is way too big")
// rockets too are a fixed world size (Cam, 2026-09-05: "do that to rockets as well"): a big rocket, a smaller interceptor
const ROCKET_M = 100, INTERCEPTOR_M = 60;
const MERGE_PX = 40; // two flights closer than this on screen draw as one model (about half a rocket)
/** Metres per CSS pixel at this zoom and latitude — MapLibre zooms are 512 px tiles (an earlier 256 px constant drew everything twice the intended size). */
const metresPerPx = (zoom: number, lat: number) => (40_075_016.7 * Math.cos((lat * Math.PI) / 180)) / (512 * Math.pow(2, zoom));
const MAX_FLIGHTS = 4096; // a world war has thousands up at once (1,937 in view on 2026-09-03)
// the size zoom lags the real zoom by up to ZOOM_STEP, so a pinch does not re-scale every frame (Cam: "let them lag a bit behind")
const ZOOM_STEP = 0.5;
// A model's ground footprint as a fraction of its height — a blocky character is far narrower than
// it is tall, so half the height would shove them absurdly far apart.
const FOOTPRINT = 0.3;
// Separation is O(n^2) per pass and runs on every camera move. Above this many models in view they
// are pixels anyway, so the pass is skipped rather than allowed to cost frames.
const SEPARATE_MAX = 240;

type Built = { geometry: THREE.BufferGeometry; texture: THREE.Texture | null; height: number; vertexColors?: boolean };
export const STRUCT_MIN_ZOOM = 12;

// ---- procedural site models (Cam, 2026-09-03: "make your own models") -------------------------------
// Low-poly primitives with baked vertex colours so each site is one geometry and one instanced draw.
const bearingTo = (a: Pos, b: Pos) => { const r = Math.PI / 180, la1 = a.lat * r, la2 = b.lat * r, dLon = (b.lon - a.lon) * r; return Math.atan2(Math.sin(dLon) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon)) / r; };
export class ModelsLayer implements CustomLayerInterface {
  id = "models-gl";
  type = "custom" as const;
  renderingMode = "3d" as const;
  private map!: MLMap;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.Camera();
  private built = new Map<string, Built>();
  private meshes = new Map<string, THREE.InstancedMesh>();
  private wireframe = false;
  private loading = false;
  private lastKey = "";
  private sizeZoom = NaN;
  private origin = new THREE.Vector3(); // world-space origin the instance matrices are relative to
  private originT = new THREE.Matrix4();
  visible = false;
  instances = 0;
  /**
   * Fired once the 18 character GLBs and the procedural geometry are all built.
   *
   * ⚠️ Whoever hides the flat sprites MUST listen to this. The sprite/model handover is decided by
   * `instances > 0`, and `instances` is zero until ~2 MB of GLB has arrived — so a handover computed
   * before this fires is computed against an empty layer and never revisited. That is a race against
   * the HTTP cache: warm loads win it, cold loads lose it, and the result changes per page load.
   */
  onReady: (() => void) | null = null;
  ready = false;
  readonly off = (() => { try { return localStorage.getItem("launch:models") === "off"; } catch { return false; } })();
  /** For the diagnostics readout: how many distinct models actually loaded (18 characters + sites + rockets). */
  get builtCount() { return this.built.size; }

  onAdd(map: MLMap, gl: WebGL2RenderingContext) {
    this.map = map;
    this.renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
    this.renderer.autoClear = false;
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x223344, 1.4));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2); sun.position.set(0.4, 1, 0.6); this.scene.add(sun);
    void this.loadAll();
  }
  onRemove() {
    for (const m of this.meshes.values()) { this.scene.remove(m); m.dispose(); }
    this.meshes.clear();
    this.renderer?.dispose(); this.renderer = null;
  }

  private async loadAll() {
    if (this.loading) return; this.loading = true;
    for (const kind of ["missileSite", "nuclearSite", "samSite", "oreMine", "sentryGun"] as const) {
      const geometry = buildSite(kind); const bb = geometry.boundingBox!; const height = bb.max.y - bb.min.y || 1;
      geometry.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2);
      this.built.set(`site-${kind}`, { geometry, texture: null, height, vertexColors: true });
    }
    for (const kind of FLIGHT_KINDS) {
      const geometry = buildRocket(kind); const bb = geometry.boundingBox!; const height = bb.max.y - bb.min.y || 1;
      geometry.translate(-(bb.min.x + bb.max.x) / 2, -(bb.min.y + bb.max.y) / 2, -(bb.min.z + bb.max.z) / 2); // centred
      this.built.set(kind, { geometry, texture: null, height, vertexColors: true });
    }
    const loader = new GLTFLoader();
    await Promise.all(CHARACTERS.map(async (name) => {
      try {
        const gltf = await loader.loadAsync(`/models/characters/${name}.glb`);
        gltf.scene.updateMatrixWorld(true);
        const parts: THREE.BufferGeometry[] = []; let texture: THREE.Texture | null = null;
        gltf.scene.traverse((o: any) => {
          if (!o.isMesh) return;
          const g = (o.geometry as THREE.BufferGeometry).clone(); g.applyMatrix4(o.matrixWorld);
          for (const k of Object.keys(g.attributes)) if (k !== "position" && k !== "normal" && k !== "uv") g.deleteAttribute(k); // instancing wants identical attribute sets
          parts.push(g);
          const mat = Array.isArray(o.material) ? o.material[0] : o.material; if (mat?.map && !texture) texture = mat.map;
        });
        const geometry = mergeGeometries(parts, false); if (!geometry) return;
        geometry.computeBoundingBox();
        const bb = geometry.boundingBox!; const height = bb.max.y - bb.min.y || 1;
        geometry.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2); // feet at the origin
        this.built.set(name, { geometry, texture, height });
      } catch (e) { console.warn("[models] failed to load", name, e); }
    }));
    this.loading = false;
    this.ready = true;
    this.lastKey = ""; this.update(true);
    // update() has just set `instances`; only now can the sprite/model handover be decided correctly.
    this.onReady?.();
  }

  private material(b: Built): THREE.Material {
    if (this.wireframe) return new THREE.MeshBasicMaterial({ color: 0x3dff7e, wireframe: true });
    if (b.vertexColors) return new THREE.MeshLambertMaterial({ vertexColors: true });
    return new THREE.MeshLambertMaterial({ map: b.texture ?? undefined, color: b.texture ? 0xffffff : 0x8b9bb0 });
  }
  setTheme(settings: MapSettings) {
    const wf = settings.theme === "retro";
    if (wf === this.wireframe) return;
    this.wireframe = wf;
    for (const [name, m] of this.meshes) { const b = this.built.get(name); if (b) { (m.material as THREE.Material).dispose(); m.material = this.material(b); } }
    this.map?.triggerRepaint();
  }

  private modelFor(id: number) { return characterFor(id); }

  /** Rebuild instance matrices. Cheap enough to call on every move-end and player change. */
  structuresVisible = false;
  update(force = false, playersVisible = true, filters: Record<string, boolean> = {}) {
    if (this.off || !this.map || this.built.size === 0) return;
    const zoom = this.map.getZoom();
    const showPlayers = playersVisible && zoom >= MODEL_MIN_ZOOM;
    const showSites = zoom >= STRUCT_MIN_ZOOM;
    const show = showPlayers || showSites;
    this.structuresVisible = showSites;
    this.stepZoom(zoom); // before any early return, so rockets at low zoom size from the current zoom too
    const c = this.map.getCenter();
    if (this.setOrigin(c.lng, c.lat)) force = true; // every static matrix is relative to the origin: if it moves, they are all rebuilt in this same call
    if (this.visible !== show) { this.visible = show; this.map.triggerRepaint(); }
    if (!show) { for (const [name, m] of this.meshes) if (!(FLIGHT_KINDS as readonly string[]).includes(name)) m.visible = false; this.instances = 0; return; }
    const key = `${zoom.toFixed(3)}|${c.lng.toFixed(5)}|${c.lat.toFixed(5)}|${this.map.getBearing().toFixed(1)}|${this.map.getPitch().toFixed(1)}|${world.staticVersion}|${showSites}`;
    if (!force && key === this.lastKey) return;
    this.lastKey = key;
    const unit = this.unitM();
    const sizeM = unit * CHAR_UNITS;
    // a site folded into a cluster octagon has no model of its own; only the points MapLibre currently draws unclustered get one
    const loose = new Set<number>();
    if (showSites) for (const f of this.map.querySourceFeatures("structures", { filter: ["!", ["has", "point_count"]] } as any)) loose.add(Number(f.properties?.id));
    const b = this.map.getBounds(); const margin = 0.2 * (b.getNorth() - b.getSouth());
    const inView = (p: Pos) => zoom < 4 || (p.lat >= b.getSouth() - margin && p.lat <= b.getNorth() + margin && p.lon >= b.getWest() - margin && p.lon <= b.getEast() + margin);
    const groups = new Map<string, { id: number; pos: Pos; size: number; alt: number }[]>();
    const push = (name: string, id: number, pos: Pos, size: number, alt = 0) => (groups.get(name) ?? groups.set(name, []).get(name)!).push({ id, pos, size, alt });
    if (showPlayers) for (const p of world.players.values()) { if (!p.pos || p.awol || !inView(p.pos)) continue; push(this.modelFor(p.id), p.id, p.pos, sizeM); }
    if (showSites) {
      for (const st of world.structures.values()) {
        if (filters[st.kind] === false || !loose.has(st.id) || !inView(st.pos)) continue; // sentry guns have a model now (Cam, 2026-09-05)
        push(`site-${st.kind === "missileSite" ? (st.nuclear ? "nuclearSite" : "missileSite") : st.kind}`, st.id, st.pos, unit);
      }
    }
    // Nudge overlapping ground models apart — a player standing on their own turret (Cam,
    // 2026-09-07). Only the MODEL moves; the marker layers keep drawing at the true position, so a
    // disc still marks where the site really is. Done across all groups at once, because the pairs
    // that overlap are a player and a site, which live in different groups.
    const flat: { name: string; e: { id: number; pos: Pos; size: number; alt: number } }[] = [];
    for (const [name, list] of groups) for (const e of list) flat.push({ name, e });
    if (flat.length > 1 && flat.length <= SEPARATE_MAX) {
      const moved = separated(flat.map(({ e }) => ({ id: e.id, lat: e.pos.lat, lon: e.pos.lon, radiusM: e.size * FOOTPRINT })));
      for (let i = 0; i < flat.length; i++) flat[i].e.pos = { lat: moved[i].lat, lon: moved[i].lon };
    }
    const mat = new THREE.Matrix4(), place = new THREE.Matrix4(), yaw = new THREE.Matrix4(), scale = new THREE.Matrix4();
    const tr = (this.map as any).transform;
    let total = 0;
    for (const [name, built] of this.built) {
      if ((FLIGHT_KINDS as readonly string[]).includes(name)) continue; // rockets are placed per frame in updateFlights()
      let mesh = this.meshes.get(name);
      if (!mesh) { mesh = new THREE.InstancedMesh(built.geometry, this.material(built), MAX_PER_MODEL); mesh.frustumCulled = false; mesh.count = 0; this.scene.add(mesh); this.meshes.set(name, mesh); }
      const list = groups.get(name) ?? [];
      const n = Math.min(list.length, MAX_PER_MODEL);
      for (let i = 0; i < n; i++) {
        const { id, pos, size, alt } = list[i];
        place.fromArray(tr.getMatrixForModel({ lng: pos.lon, lat: pos.lat }, alt) as ArrayLike<number>);
        place.elements[12] -= this.origin.x; place.elements[13] -= this.origin.y; place.elements[14] -= this.origin.z; // float64 here, small numbers from now on
        const s = size / built.height;
        scale.makeScale(s, s, s); yaw.makeRotationY(((id * 137) % 360) * (Math.PI / 180));
        mat.copy(place).multiply(yaw).multiply(scale);
        mesh.setMatrixAt(i, mat);
      }
      mesh.count = n; mesh.instanceMatrix.needsUpdate = true; mesh.visible = n > 0;
      total += n;
    }
    this.instances = total;
    this.map.triggerRepaint();
  }

  /** The world-space origin instance matrices are relative to: the ground at the (rounded) view centre. Recomputed from the
   *  transform every call, because the same lng/lat is a different point once MapLibre swaps its globe transform for mercator
   *  at zoom 12 (a stale globe-space origin made the depth ratio below read 0.001 and clamp every rocket to a quarter size). */
  /** ⚠ Only update() may move the origin, and it rebuilds every static matrix when it does. updateFlights() used to call this
   *  every frame: during a pan the origin slid with the map centre and the sites slid with the camera until move-end
   *  snapped them back (Cam, 2026-09-04: "when I pan they stick to their old spot and then catch up"). Returns true if moved. */
  private setOrigin(lng: number, lat: number): boolean {
    const o = (this.map as any).transform.getMatrixForModel({ lng: +lng.toFixed(5), lat: +lat.toFixed(5) }, 0) as ArrayLike<number>;
    if (o[12] === this.origin.x && o[13] === this.origin.y && o[14] === this.origin.z) return false;
    this.origin.set(o[12], o[13], o[14]); this.originT.makeTranslation(this.origin.x, this.origin.y, this.origin.z);
    return true;
  }
  /** The size zoom follows the real zoom in half-level steps (NaN-safe: the first call sets it). */
  private stepZoom(zoom: number) { if (!(Math.abs(zoom - this.sizeZoom) < ZOOM_STEP)) this.sizeZoom = zoom; }
  private unitM() { return UNIT_M; }

  /** Rockets and interceptors, every frame: on the arc (altitude mode, poses from the arcs layer) or at sea level. Nose along the heading. */
  flightInstances = 0;
  private fA = new THREE.Vector3(); private fB = new THREE.Vector3(); private fUp = new THREE.Vector3(); private fX = new THREE.Vector3(); private fZ = new THREE.Vector3(); private fM = new THREE.Matrix4();
  updateFlights(now: number, filters: MapFilters, arcs: ArcsLayer, settings: MapSettings) {
    if (this.off || !this.map || this.built.size === 0) return;
    const showM = filters.missiles !== false && !settings.lowDetail, showI = filters.interceptors !== false && !settings.lowDetail;
    const tr = (this.map as any).transform;
    const occluded = (lon: number, lat: number) => typeof tr.isLocationOccluded === "function" && tr.isLocationOccluded(new maplibregl.LngLat(lon, lat));
    this.stepZoom(this.map.getZoom());
    if (this.origin.lengthSq() === 0) { const c = this.map.getCenter(); this.setOrigin(c.lng, c.lat); } // first frame only; after that the origin belongs to update()
    const rocketM = ROCKET_M, interceptorM = INTERCEPTOR_M;
    const M = this.lastMain; // for the on-screen merge test below
    const lists: Record<FlightKind, { pos: Pos; alt: number; head: Pos; alt1: number; size: number }[]> = { rocket: [], "rocket-nuclear": [], "rocket-ecm": [], interceptor: [] };
    const altitudeMode = filters.arcs !== false && !arcs.off && !settings.lowDetail;
    if (showM) {
      if (altitudeMode) {
        for (const p of arcs.poses) {
          const ll = new maplibregl.MercatorCoordinate(p.x, p.y).toLngLat(), ll1 = new maplibregl.MercatorCoordinate(p.x1, p.y1).toLngLat();
          if (occluded(ll.lng, ll.lat)) continue;
          lists[p.tex === 1 ? "rocket-nuclear" : p.tex === 2 ? "rocket-ecm" : "rocket"].push({ pos: { lon: ll.lng, lat: ll.lat }, alt: p.alt, head: { lon: ll1.lng, lat: ll1.lat }, alt1: p.alt1, size: rocketM });
        }
      } else {
        for (const m of world.missiles.values()) {
          const f = missileFlight(m, now); if (!f || occluded(f.pos.lon, f.pos.lat)) continue;
          const head = f.remainingKm > 0.5 ? move(f.pos, bearingTo(f.pos, f.target), Math.min(f.remainingKm, 5)) : f.target;
          lists[f.type.nuclear ? "rocket-nuclear" : f.type.ecm ? "rocket-ecm" : "rocket"].push({ pos: f.pos, alt: 0, head, alt1: 0, size: rocketM });
        }
      }
    }
    if (showI) {
      for (const i of world.interceptors.values()) {
        if (!i.pos || occluded(i.pos.lon, i.pos.lat)) continue;
        const m = i.targetId !== undefined ? world.missiles.get(i.targetId) : null;
        const f = m ? missileFlight(m, now) : null;
        const km = f ? distanceKm(i.pos, f.pos) : 0;
        const head = f && km > 0.05 ? move(i.pos, bearingTo(i.pos, f.pos), Math.min(km, 5)) : { lon: i.pos.lon, lat: i.pos.lat + 0.01 };
        lists.interceptor.push({ pos: i.pos, alt: 0, head, alt1: 0, size: interceptorM });
      }
    }
    // rockets that touch on screen become one model (Cam, 2026-09-03): the first one wins, later ones within MERGE_PX are dropped.
    // Rockets are placed before interceptors, so a rocket beats an interceptor for the spot.
    const cell = MERGE_PX, buckets = new Map<string, number[][]>();
    const screen = (wx: number, wy: number, wz: number): [number, number] | null => {
      if (!M) return null; const cw = M[3] * wx + M[7] * wy + M[11] * wz + M[15]; if (!(cw > 0)) return null;
      return [(M[0] * wx + M[4] * wy + M[8] * wz + M[12]) / cw * 500, (M[1] * wx + M[5] * wy + M[9] * wz + M[13]) / cw * 500]; // clip → ~px (1000 px wide reference; only relative distances matter)
    };
    const crowded = (wx: number, wy: number, wz: number) => {
      const s = screen(wx, wy, wz); if (!s) return false;
      const cx = Math.floor(s[0] / cell), cy = Math.floor(s[1] / cell);
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) { const b = buckets.get(`${cx + dx},${cy + dy}`); if (b) for (const q of b) if (Math.hypot(q[0] - s[0], q[1] - s[1]) < cell) return true; }
      const k = `${cx},${cy}`; (buckets.get(k) ?? buckets.set(k, []).get(k)!).push(s); return false;
    };
    let total = 0;
    for (const kind of FLIGHT_KINDS) {
      const built = this.built.get(kind)!; let mesh = this.meshes.get(kind);
      if (!mesh) { mesh = new THREE.InstancedMesh(built.geometry, this.material(built), MAX_FLIGHTS); mesh.frustumCulled = false; mesh.count = 0; this.scene.add(mesh); this.meshes.set(kind, mesh); }
      const list = lists[kind]; let k = 0;
      for (let i = 0; i < list.length && k < MAX_FLIGHTS; i++) {
        const { pos, alt, head, alt1, size } = list[i];
        const A = tr.getMatrixForModel({ lng: pos.lon, lat: pos.lat }, alt) as ArrayLike<number>;
        if (crowded(A[12], A[13], A[14])) continue;
        const B = tr.getMatrixForModel({ lng: head.lon, lat: head.lat }, alt1) as ArrayLike<number>;
        this.fA.set(A[12] - this.origin.x, A[13] - this.origin.y, A[14] - this.origin.z); // float64 until here
        this.fB.set(B[12] - A[12], B[13] - A[13], B[14] - A[14]);
        this.fUp.set(A[4], A[5], A[6]).normalize();
        if (this.fB.lengthSq() < 1e-30) this.fB.copy(this.fUp); else this.fB.normalize();  // heading; straight up if the two points coincide
        this.fX.crossVectors(this.fUp, this.fB); if (this.fX.lengthSq() < 1e-12) this.fX.set(A[0], A[1], A[2]); this.fX.normalize();
        this.fZ.crossVectors(this.fX, this.fB);
        const s = Math.hypot(A[0], A[1], A[2]) * (size / built.height); // world units per metre × metres per model unit
        this.fM.makeBasis(this.fX.multiplyScalar(s), this.fB.multiplyScalar(s), this.fZ.multiplyScalar(s)); this.fM.setPosition(this.fA);
        mesh.setMatrixAt(k++, this.fM);
      }
      mesh.count = k; mesh.instanceMatrix.needsUpdate = true; mesh.visible = k > 0; total += k;
    }
    if (total !== this.flightInstances || total > 0) this.map.triggerRepaint();
    this.flightInstances = total;
    const show = this.visible || total > 0;
    if (show !== this.shown) { this.shown = show; this.map.triggerRepaint(); }
  }
  private shown = false;
  /** world→clip from this layer's own last render (the arcs layer's copy can be stale when it has nothing to draw) */
  lastMain: Float64Array | null = null;

  render(_gl: WebGL2RenderingContext, args: any) {
    if (this.off || !this.renderer || (this.instances === 0 && this.flightInstances === 0)) return;
    const pd = args.defaultProjectionData; if (!pd) return;
    this.lastMain = Float64Array.from(pd.mainMatrix as ArrayLike<number>);
    this.camera.projectionMatrix.fromArray(pd.mainMatrix).multiply(this.originT); // world→clip × (origin shift), in float64
    this.camera.matrixWorldInverse.identity();
    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);
  }
}
